import React, { useEffect, useState } from "react";
import { ShieldCheckIcon as ShieldCheck, ShieldStarIcon as ShieldStar, CopyIcon as Copy, CheckCircleIcon as CheckCircle, WarningIcon as Warning, LockIcon as Lock, KeyIcon as Key } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/**
 * 2FA Settings card.
 *
 * Inline tile for /settings — three states:
 *  1. Disabled (no 2FA on the account) → "Enable 2FA" button opens
 *     setup dialog with QR code + secret + verification field.
 *  2. Setup-in-progress → user has scanned QR, must verify a 6-digit
 *     code; on success we display the 8 recovery codes ONCE and a
 *     mandatory acknowledgement.
 *  3. Enabled → green confirmation + "Disable" button (requires code).
 *
 * The whole tile is gated to the current user's session — no admin
 * surface, just self-service.
 */
export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null); // {enabled: bool}
  const [setupData, setSetupData] = useState(null); // {provisioning_uri, secret, qr_png_base64}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  const refresh = () =>
    api.get("/auth/2fa/status").then((r) => setStatus(r.data));

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupData(r.data);
      setRecoveryCodes(null);
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", { code: code.trim() });
      setRecoveryCodes(r.data.recovery_codes);
      toast.success("2FA enabled — save the recovery codes now");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!disableCode.trim()) {
      toast.error("Enter your current code or a recovery code.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      toast.success("2FA disabled");
      setDisableOpen(false);
      setDisableCode("");
      setSetupData(null);
      setRecoveryCodes(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setSecretCopied(true);
      toast.success("Secret copied");
      setTimeout(() => setSecretCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — type it manually.");
    }
  };

  const downloadRecovery = () => {
    if (!recoveryCodes) return;
    const blob = new Blob(
      [
        "Armstrong K9 Registry — 2FA recovery codes\n\n",
        "Each of these codes can be used ONCE if you lose your authenticator app.\n",
        "Store them somewhere safe (password manager, locked drawer).\n\n",
        ...recoveryCodes.map((c, i) => `  ${i + 1}. ${c}\n`),
      ],
      { type: "text/plain;charset=utf-8" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `armstrong-2fa-recovery-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!status) return null;

  const isEnabled = status.enabled;

  return (
    <div
      className="rounded-xl border border-[#E5E5E5] bg-white p-5"
      data-testid="two-factor-settings"
    >
      <div className="flex items-start gap-4">
        <span
          className={`rounded-full p-2.5 flex-shrink-0 ${
            isEnabled
              ? "bg-[#EEFBEC] text-[#0F8043]"
              : "bg-[#FAFAFA] text-[#6B6B6B]"
          }`}
        >
          {isEnabled ? <ShieldCheck size={18} weight="duotone" /> : <Lock size={18} weight="duotone" />}
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-bold text-ink">
                Two-factor authentication
                {isEnabled && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[#0F8043] bg-[#EEFBEC] border border-[#8ED08E] px-2 py-0.5 rounded-full"
                    data-testid="2fa-enabled-pill"
                  >
                    <CheckCircle size={11} weight="fill" />
                    Enabled
                  </span>
                )}
              </p>
              <p className="text-[12.5px] text-[#6B6B6B] mt-0.5 max-w-lg">
                When enabled, sensitive operations (transferring a dog,
                releasing escrow funds, disabling 2FA) require a fresh
                code from your authenticator app. Your normal login
                isn't affected.
              </p>
            </div>
            {!isEnabled && !setupData && (
              <Button
                onClick={startSetup}
                disabled={busy}
                size="sm"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                data-testid="2fa-enable-btn"
              >
                <ShieldStar size={13} className="mr-1.5" weight="duotone" />
                Enable 2FA
              </Button>
            )}
            {isEnabled && (
              <Button
                onClick={() => setDisableOpen(true)}
                disabled={busy}
                size="sm"
                variant="outline"
                className="rounded-full text-crimson border-[#C1272D] hover:bg-[#FDEEEE]"
                data-testid="2fa-disable-btn"
              >
                Disable 2FA
              </Button>
            )}
          </div>

          {/* Setup in progress */}
          {setupData && !isEnabled && (
            <div
              className="mt-4 rounded-lg border border-[#E5D08A] bg-[#FBF7EC] p-4"
              data-testid="2fa-setup-panel"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8A6A00] mb-2">
                Step 1 · Scan with your authenticator app
              </p>
              <p className="text-[12.5px] text-[#5C4A00] mb-3">
                Use Google Authenticator, 1Password, Authy, or any
                TOTP-compatible app. Scan the QR or paste the secret manually.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <img
                  src={`data:image/png;base64,${setupData.qr_png_base64}`}
                  alt="2FA QR code"
                  className="w-44 h-44 rounded-md border border-[#E5D08A] bg-white"
                  data-testid="2fa-qr-img"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">Manual entry secret</p>
                  <div className="flex items-center gap-2 mb-1">
                    <code
                      className="font-mono text-[11.5px] text-ink bg-white border border-[#E5D08A] rounded px-2 py-1.5 select-all break-all"
                      data-testid="2fa-secret-text"
                    >
                      {setupData.secret}
                    </code>
                    <button
                      type="button"
                      onClick={copySecret}
                      className="flex-shrink-0 text-[11px] text-crimson font-bold inline-flex items-center gap-1 hover:text-ink"
                      data-testid="2fa-secret-copy-btn"
                    >
                      {secretCopied ? <CheckCircle size={11} weight="fill" /> : <Copy size={11} weight="bold" />}
                      {secretCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="text-[10px] text-[#6B6B6B]">Issuer: {setupData.issuer}</p>
                </div>
              </div>

              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8A6A00] mb-2">
                Step 2 · Confirm with a code
              </p>
              <div className="flex gap-2 items-center">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  maxLength={10}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-32 font-mono text-base"
                  data-testid="2fa-verify-input"
                />
                <Button
                  onClick={verify}
                  disabled={busy}
                  size="sm"
                  className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                  data-testid="2fa-verify-btn"
                >
                  Verify &amp; enable
                </Button>
                <Button
                  onClick={() => { setSetupData(null); setCode(""); }}
                  size="sm"
                  variant="ghost"
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Recovery codes shown ONCE after enable */}
          {recoveryCodes && (
            <div
              className="mt-4 rounded-lg border border-[#0F8043] bg-[#EEFBEC] p-4"
              data-testid="2fa-recovery-panel"
            >
              <div className="flex items-center gap-2 mb-2">
                <Key size={16} weight="duotone" className="text-[#0F8043]" />
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F8043]">
                  Recovery codes — save these now
                </p>
              </div>
              <p className="text-[12.5px] text-[#1F4F1F] mb-3">
                Each code can be used <strong>once</strong> if you lose
                your authenticator app. They're shown <strong>only this
                time</strong> — copy them, print them, save in a password
                manager. We can't show them again.
              </p>
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 bg-white rounded-md border border-[#8ED08E] p-3"
                data-testid="2fa-recovery-codes"
              >
                {recoveryCodes.map((c, i) => (
                  <code
                    key={i}
                    className="font-mono text-[12.5px] text-ink select-all"
                  >
                    {c}
                  </code>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={downloadRecovery}
                  size="sm"
                  className="rounded-full bg-[#0F8043] hover:bg-[#0a6634] text-white"
                  data-testid="2fa-recovery-download-btn"
                >
                  Download as .txt
                </Button>
                <Button
                  onClick={() => { setRecoveryCodes(null); setSetupData(null); }}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  data-testid="2fa-recovery-acknowledge-btn"
                >
                  I've saved them — done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disable confirmation dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral flex items-center gap-2">
              <Warning size={18} weight="duotone" className="text-crimson" />
              Disable 2FA?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-[#5C5C5C] leading-relaxed mb-3">
            Confirm with a current 6-digit code or one of your recovery
            codes. Once disabled, transferring a dog or releasing
            escrow funds will no longer require an authenticator code.
          </p>
          <Input
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="123456 or recovery code"
            inputMode="text"
            autoComplete="one-time-code"
            className="font-mono"
            data-testid="2fa-disable-code-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={disable}
              disabled={busy}
              className="rounded-full bg-[#C1272D] hover:bg-[#a02124] text-white"
              data-testid="2fa-disable-confirm-btn"
            >
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
